import { useQueryClient } from "@tanstack/react-query";

import { API_CACHE_KEYS } from "api/cacheKeys";
import { useCustomMutation } from "api/useCustomMutation";
import { useCustomQuery } from "api/useCustomQuery";
import { JANZU_URL } from "config/config";
import { NoteOwner, NotesResponse } from "types/notes";

export type GetNotesPrams = {
  detectorId?: string;
};

type UseGetNotesPrams = {
  params?: GetNotesPrams;
};

const basePath = `${JANZU_URL}reef-api/notes`;

const useGetNotes = ({ params }: UseGetNotesPrams = {}) => {
  const searchParams = new URLSearchParams();

  if (params?.detectorId !== undefined) {
    searchParams.append("detector_id", params.detectorId);
  }

  const url = `${basePath}?${searchParams.toString()}`;

  return useCustomQuery<NotesResponse>(API_CACHE_KEYS.notes.list(params), url.toString(), {
    enabled: params?.detectorId !== undefined,
  });
};

type CreateNotePrams = {
  detectorId?: string;
};

type UseCreateNotePrams = {
  params?: CreateNotePrams;
};

export type CreateNoteData = {
  content: string;
  is_pinned: boolean;
  order: number;
  image?: File | null;
  note_type: NoteOwner;
};

const useCreateNote = ({ params }: UseCreateNotePrams = {}) => {
  const searchParams = new URLSearchParams();
  const queryClient = useQueryClient();

  if (params?.detectorId !== undefined) {
    searchParams.append("detector_id", params.detectorId);
  }

  const url = `${basePath}?${searchParams.toString()}`;

  return useCustomMutation<NotesResponse, CreateNoteData>(url.toString(), "POST", {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: API_CACHE_KEYS.notes.all,
      });
    },
  });
};

// Bulk create notes using FormData (for multiple notes at once)
type BulkCreateNotesData = FormData & {
  detectorId?: string;
};

const useBulkCreateNotes = () => {
  const queryClient = useQueryClient();

  return useCustomMutation<NotesResponse, BulkCreateNotesData>(
    (data) => {
      const searchParams = new URLSearchParams();
      if (data.detectorId) {
        searchParams.append("detector_id", data.detectorId);
      }
      return `${basePath}?${searchParams.toString()}`;
    },
    "POST",
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: API_CACHE_KEYS.notes.all,
        });
      },
    },
    ["detectorId"]
  );
};

type UpdateNotesOrderData = Record<string, number>;

const useUpdateNotesOrder = () => {
  const url = `${basePath}/order`;
  const queryClient = useQueryClient();

  return useCustomMutation<NotesResponse, UpdateNotesOrderData>(url, "PUT", {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: API_CACHE_KEYS.notes.all,
      });
    },
  });
};

type UpdateNoteData = {
  id: string;
  content?: string;
  is_pinned?: boolean;
  order?: number;
  image?: File | null;
  note_type?: NoteOwner;
};

const useUpdateNote = () => {
  const queryClient = useQueryClient();

  return useCustomMutation<NotesResponse, UpdateNoteData>(
    ({ id }) => `${basePath}/${id}`,
    "PATCH",
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: API_CACHE_KEYS.notes.all,
        });
      },
    },
    ["id"]
  );
};

type DeleteNoteData = {
  id: string;
};

const useDeleteNote = () => {
  const queryClient = useQueryClient();

  return useCustomMutation<NotesResponse, DeleteNoteData>(({ id }) => `${basePath}/${id}`, "DELETE", {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: API_CACHE_KEYS.notes.all,
      });
    },
  });
};

type DeleteNoteImageData = {
  id: string;
};

const useDeleteNoteImage = () => {
  const queryClient = useQueryClient();

  return useCustomMutation<NotesResponse, DeleteNoteImageData>(({ id }) => `${basePath}/${id}/image`, "DELETE", {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: API_CACHE_KEYS.notes.all,
      });
    },
  });
};

type UseNotesParams = {
  detectorId?: string;
};

export const useNotes = ({ detectorId }: UseNotesParams = {}) => {
  const notes = useGetNotes({ params: { detectorId } });
  const createNote = useCreateNote({ params: { detectorId } });
  const bulkCreateNotes = useBulkCreateNotes();
  const updateNotesOrder = useUpdateNotesOrder();
  const updateNote = useUpdateNote();
  const deleteNote = useDeleteNote();
  const deleteNoteImage = useDeleteNoteImage();

  return {
    // Get operations
    notes: notes.data,
    isLoadingNotes: notes.isLoading,
    notesError: notes.error,

    // Create operation
    createNote: createNote.mutate,
    createNoteAsync: createNote.mutateAsync,
    isCreatingNote: createNote.isLoading,
    createNoteError: createNote.error,

    // Bulk create operation
    bulkCreateNotes: bulkCreateNotes.mutate,
    bulkCreateNotesAsync: bulkCreateNotes.mutateAsync,
    isBulkCreatingNotes: bulkCreateNotes.isLoading,
    bulkCreateNotesError: bulkCreateNotes.error,

    // Update operation
    updateNote: updateNote.mutate,
    isUpdatingNote: updateNote.isLoading,
    updateNoteError: updateNote.error,

    // Update order operation
    updateNotesOrder: updateNotesOrder.mutate,
    isUpdatingNotesOrder: updateNotesOrder.isLoading,
    updateNotesOrderError: updateNotesOrder.error,

    // Delete operation
    deleteNote: deleteNote.mutate,
    isDeletingNote: deleteNote.isLoading,
    deleteNoteError: deleteNote.error,

    // Delete image operation
    deleteNoteImage: deleteNoteImage.mutate,
    isDeletingNoteImage: deleteNoteImage.isLoading,
    deleteNoteImageError: deleteNoteImage.error,

    // Combined loading state
    isLoading: updateNotesOrder.isLoading || updateNote.isLoading || deleteNote.isLoading || deleteNoteImage.isLoading,
  };
};
